import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Skull, ZoomOut } from 'lucide-react'
import {
  EncounterAbilityRow,
  PhaseBoundaryHandle,
  PhaseDivider,
} from '../hooks/useTeamPlanComputedData'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useObservedWidth from '../hooks/useObservedWidth'
import useTimelineTicks from '../hooks/useTimelineTicks'
import useVisibleTimelineTicks from '../hooks/useVisibleTimelineTicks'
import { formatTimelineTimestamp, roundedBarPath } from './timelineMath'
import { buildPhaseLabel } from './AssignmentPhaseTimestampBadge'
import { resolveEntrySegments } from './timelineSegments'
import SpellIcon from './SpellIcon'
import TimelineTooltipText from './TimelineTooltipText'
import { EncounterAbilitySegmentType } from 'app/frontend/types/custom/EncounterEvents'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import useTimelineZoomInteraction from '../hooks/useTimelineZoomInteraction'
import TimelineDragSelectionOverlay from './TimelineDragSelectionOverlay'

type TimelineContainerProps = {
  maxTime: number
  phaseDividers: PhaseDivider[]
  phaseBoundaryHandles: PhaseBoundaryHandle[]
  rows: EncounterAbilityRow[]
  hoverTime?: number | null
  onPhaseBoundaryDragStart?: (boundaryIndex: number, at: number) => void
  onPhaseBoundaryDrag?: (boundaryIndex: number, nextAt: number) => void
  onPhaseBoundaryDragEnd?: (boundaryIndex: number, nextAt: number) => void
  onSwapPhase?: (displayIndex: number, direction: 'left' | 'right') => void
  onResetPhaseDuration?: (phaseIndex: number) => void
  disableZoomInteraction?: boolean
  castMarkers?: {
    id: string
    at: number
    label: string
    ignored?: boolean
    tooltip: string
    disableTooltip?: boolean
    onPointerDown?: (event: React.PointerEvent<SVGRectElement>) => void
    onClick?: (event: React.MouseEvent<SVGRectElement>) => void
    onContextMenu?: (event: React.MouseEvent<SVGRectElement>) => void
  }[]
  enableAddCastInteraction?: boolean
  onAddCastAt?: (at: number) => void
  emptyMessage?: string
  className?: string
  deathsEnabled?: boolean
  deathMarkers?: {
    at: number
    count: number
    pullCount: number
    opacity: number
    strokeWidth: number
  }[]
}

const ROW_HEIGHT = 20
const ROW_GAP = 0
const BAR_RADIUS = 2
const PHASE_LABEL_AREA_HEIGHT = 24
const TICK_LABEL_AREA_HEIGHT = 20
const EMPTY_STATE_ROWS_HEIGHT = 128
const PHASE_CARD_GAP_PX = 4
const EDGE_LABEL_COLLISION_PADDING_SECONDS = 8
const CAST_LABEL_MIN_GAP_SECONDS = 7
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

type ZoomEdgeControlProps = {
  x: number
  y: number
  width: number
  label: string
  onReset: (event: React.MouseEvent | React.PointerEvent) => void
}

type HoveredTimelineSegment = {
  key: string
  x: number
  y: number
  width: number
  spellId: number
  abilityName: string
  abilityColor: string
  label?: string | null
  start: number
  duration: number
}

type VisibleEntrySegment = {
  key: string
  type: EncounterAbilitySegmentType
  x: number
  width: number
  color: string
  tooltip?: string | null
  start: number
  duration: number
  roundLeft: boolean
  roundRight: boolean
}

const formatSegmentDuration = (duration: number) => {
  const rounded = Math.round(duration * 10) / 10
  return `${rounded}s`
}

const EncounterEntrySegment = ({
  segment,
  y,
  hatchPatternId,
  onHover,
}: {
  segment: VisibleEntrySegment
  y: number
  hatchPatternId: string
  onHover: (segment: VisibleEntrySegment | null) => void
}) => {
  const path = roundedBarPath(
    segment.x,
    y,
    segment.width,
    ROW_HEIGHT,
    BAR_RADIUS,
    segment.roundLeft,
    segment.roundRight,
  )
  const hoverHandlers = {
    onPointerEnter: () => onHover(segment),
  }

  if (segment.type === 'LINE') {
    const midY = y + ROW_HEIGHT / 2
    const endX = segment.x + segment.width
    return (
      <g className='text-border' {...hoverHandlers}>
        <line
          x1={segment.x}
          x2={endX}
          y1={midY}
          y2={midY}
          stroke={segment.color}
          strokeWidth={2}
          strokeLinecap='round'
        />
        <line
          x1={endX}
          x2={endX}
          y1={y + 2}
          y2={y + ROW_HEIGHT - 2}
          stroke={segment.color}
          strokeWidth={2}
          strokeLinecap='round'
        />
        <rect
          x={segment.x}
          y={y}
          width={Math.max(segment.width, 6)}
          height={ROW_HEIGHT}
          fill='transparent'
        />
      </g>
    )
  }

  return (
    <g className='text-border' {...hoverHandlers}>
      <path d={path} fill={segment.color} fillOpacity={0.65} />
      {segment.type === 'STRIPED' ? (
        <path d={path} fill={`url(#${hatchPatternId})`} />
      ) : null}
      <path d={path} fill='none' stroke='currentColor' strokeWidth={1} />
    </g>
  )
}

const ZoomEdgeControl = ({
  x,
  y,
  width,
  label,
  onReset,
}: ZoomEdgeControlProps) => {
  return (
    <foreignObject x={x} y={y - 2} width={width} height={20}>
      <button
        type='button'
        className='h-4 w-full rounded-sm px-1 bg-info text-info-foreground border border-info-foreground transition-colors flex items-center gap-1 text-[12px] leading-none'
        onPointerDown={onReset}
        onClick={onReset}
      >
        <span className='truncate'>{label}</span>
        <ZoomOut className='w-3.5 h-3.5 shrink-0 ml-auto' />
      </button>
    </foreignObject>
  )
}

const REMOVED_PHASE_BUTTON_WIDTH = 22

const PhaseRemovedButton = ({
  label,
  onReset,
}: {
  label: string
  onReset?: () => void
}) => {
  const button = (
    <button
      type='button'
      className='h-4 min-w-[18px] px-1 rounded-sm text-[10px] leading-none inline-flex items-center justify-center text-muted-foreground bg-background border border-border hover:bg-muted'
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onReset?.()
      }}
    >
      {label}
    </button>
  )
  if (!onReset) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>Phase removed. Click to restore</TooltipContent>
    </Tooltip>
  )
}

const PhaseShortenedLabel = ({
  shortenedBy,
  onReset,
}: {
  shortenedBy: number
  onReset?: () => void
}) => {
  const label = (
    <span className='text-success-foreground'>{`-${shortenedBy}s`}</span>
  )
  if (!onReset) return label

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          className='text-success-foreground hover:underline'
          onPointerDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onReset()
          }}
        >
          {`-${shortenedBy}s`}
        </button>
      </TooltipTrigger>
      <TooltipContent>Phase shortened. Click to restore</TooltipContent>
    </Tooltip>
  )
}

const TimelineContainer = ({
  maxTime,
  phaseDividers,
  phaseBoundaryHandles,
  rows,
  hoverTime,
  onPhaseBoundaryDragStart,
  onPhaseBoundaryDrag,
  onPhaseBoundaryDragEnd,
  onSwapPhase,
  onResetPhaseDuration,
  disableZoomInteraction = false,
  castMarkers = [],
  enableAddCastInteraction = false,
  onAddCastAt,
  emptyMessage,
  className,
  deathsEnabled = false,
  deathMarkers = [],
}: TimelineContainerProps) => {
  const { zoom, setZoom } = useTeamPlanTimeline()
  const rootRef = useRef<HTMLDivElement>(null)
  const phaseBoundaryHandlesRef = useRef(phaseBoundaryHandles)
  const lastBoundaryDragFinishedAtRef = useRef(0)
  const visibleRangeRef = useRef({
    start: 0,
    end: maxTime,
  })
  const timelineWidth = useObservedWidth(rootRef)
  const [addHoverTime, setAddHoverTime] = useState<number | null>(null)
  const [addHoverBlocked, setAddHoverBlocked] = useState(false)
  const [hoveredSegment, setHoveredSegment] =
    useState<HoveredTimelineSegment | null>(null)
  const [resizeTooltipHandleIndex, setResizeTooltipHandleIndex] = useState<
    number | null
  >(null)
  const suppressResizeTooltipRef = useRef(false)
  const isDraggingPhaseRef = useRef(false)
  const hatchPatternId = `timeline-hatch-${useId().replace(/:/g, '')}`

  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, maxTime)
    const end = clamp(zoom?.end ?? maxTime, start + 1, maxTime)
    return { start, end }
  }, [maxTime, zoom?.end, zoom?.start])
  const isZoomed = visibleRange.start > 0 || visibleRange.end < maxTime

  useEffect(() => {
    phaseBoundaryHandlesRef.current = phaseBoundaryHandles
  }, [phaseBoundaryHandles])

  useEffect(() => {
    visibleRangeRef.current = visibleRange
  }, [visibleRange])

  const onBoundaryHandlePointerDown = useCallback(
    (event: React.PointerEvent<SVGRectElement>, boundaryIndex: number) => {
      const svg = event.currentTarget.ownerSVGElement
      if (!svg) return
      event.preventDefault()
      event.stopPropagation()

      const rect = svg.getBoundingClientRect()
      const startHandle = phaseBoundaryHandlesRef.current.find(
        (entry) => entry.index === boundaryIndex,
      )
      if (!startHandle) return

      // Delta-based mapping: convert cursor movement to time using a
      // pixels-per-second scale frozen at grab time. This lets the cursor drag
      // past the viewport edge and keep growing the phase (the timeline expands
      // to follow), instead of clamping at the visible right edge.
      const startClientX = event.clientX
      const startAt = startHandle.at
      const { min, max } = startHandle
      const visibleSpan = Math.max(
        visibleRangeRef.current.end - visibleRangeRef.current.start,
        1,
      )
      const pixelsPerSecond = rect.width > 0 ? rect.width / visibleSpan : 0
      const computeAt = (clientX: number) => {
        if (pixelsPerSecond <= 0) return Math.floor(startAt)
        const rawAt = startAt + (clientX - startClientX) / pixelsPerSecond
        return Math.floor(Math.max(min, Math.min(max, rawAt)))
      }

      onPhaseBoundaryDragStart?.(boundaryIndex, Math.floor(startAt))
      isDraggingPhaseRef.current = true
      suppressResizeTooltipRef.current = true
      setResizeTooltipHandleIndex(null)
      const onMove = (moveEvent: PointerEvent) => {
        onPhaseBoundaryDrag?.(boundaryIndex, computeAt(moveEvent.clientX))
      }

      const onUp = (upEvent: PointerEvent) => {
        onPhaseBoundaryDragEnd?.(boundaryIndex, computeAt(upEvent.clientX))
        lastBoundaryDragFinishedAtRef.current = Date.now()
        isDraggingPhaseRef.current = false
        suppressResizeTooltipRef.current = true
        setResizeTooltipHandleIndex(null)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [onPhaseBoundaryDragStart, onPhaseBoundaryDrag, onPhaseBoundaryDragEnd],
  )
  const { minorTickTimes, minuteTickTimes } = useTimelineTicks({
    maxTime,
    phaseDividers,
  })

  const minuteTickLabelTimes = useMemo(
    () =>
      minuteTickTimes.filter(
        (tickAt) =>
          tickAt + 15 <= maxTime &&
          tickAt >= visibleRange.start &&
          tickAt <= visibleRange.end &&
          (!isZoomed ||
            (Math.abs(tickAt - visibleRange.start) >
              EDGE_LABEL_COLLISION_PADDING_SECONDS &&
              Math.abs(tickAt - visibleRange.end) >
                EDGE_LABEL_COLLISION_PADDING_SECONDS)),
      ),
    [isZoomed, maxTime, minuteTickTimes, visibleRange.end, visibleRange.start],
  )

  const phaseRanges = useMemo(() => {
    let start = 0
    return phaseDividers.map((phase, displayIndex) => {
      const end = Math.max(start, Math.min(phase.at, maxTime))
      const range = {
        name: phase.name,
        start,
        end,
        displayIndex,
        phaseIndex: phase.phaseIndex,
        canMoveLeft: phase.canMoveLeft,
        canMoveRight: phase.canMoveRight,
        shortenedBy: phase.shortenedBy,
      }
      start = end
      return range
    })
  }, [maxTime, phaseDividers])

  const visiblePhaseRanges = useMemo(
    () =>
      phaseRanges
        .map((phase) => ({
          ...phase,
          visibleStart: Math.max(phase.start, visibleRange.start),
          visibleEnd: Math.min(phase.end, visibleRange.end),
        }))
        .filter((phase) => phase.visibleEnd > phase.visibleStart),
    [phaseRanges, visibleRange.end, visibleRange.start],
  )

  const removedPhaseGroups = useMemo(() => {
    const groups: Array<{
      at: number
      phases: typeof phaseRanges
    }> = []
    phaseRanges.forEach((phase) => {
      if (phase.end > phase.start) return
      const last = groups[groups.length - 1]
      if (last && last.at === phase.start) {
        last.phases.push(phase)
        return
      }
      groups.push({ at: phase.start, phases: [phase] })
    })
    return groups
  }, [phaseRanges])

  const rowsHeight = Math.max(
    rows.length * ROW_HEIGHT + Math.max(rows.length - 1, 0) * ROW_GAP,
    rows.length === 0 ? EMPTY_STATE_ROWS_HEIGHT : 0,
  )
  const rowsTop = PHASE_LABEL_AREA_HEIGHT
  const deathLabelAreaHeight = deathsEnabled ? 10 : 0
  const svgHeight =
    rowsTop + rowsHeight + TICK_LABEL_AREA_HEIGHT + deathLabelAreaHeight

  const onDragZoom = useCallback(
    (range: { start: number; end: number }) => {
      const start = Math.floor(clamp(range.start, 0, maxTime))
      const end = Math.floor(clamp(range.end, start + 1, maxTime))
      if (end - start < 1) return
      if (start <= 0 && end >= maxTime) {
        setZoom(null)
        return
      }
      setZoom({ start, end })
    },
    [maxTime, setZoom],
  )

  const zoomToPhase = useCallback(
    (phase: { start: number; end: number }) => {
      const start = Math.floor(clamp(phase.start, 0, maxTime))
      const end = Math.floor(clamp(phase.end, start + 1, maxTime))
      if (end - start < 1) return
      if (
        (start <= 0 && end >= maxTime) ||
        (visibleRange.start === start && visibleRange.end === end)
      ) {
        setZoom(null)
        return
      }
      setZoom({ start, end })
    },
    [maxTime, setZoom, visibleRange.end, visibleRange.start],
  )

  const findPhaseAtTime = useCallback(
    (time: number) => {
      if (phaseRanges.length === 0) return null
      return (
        phaseRanges.find((phase, index) => {
          if (index === phaseRanges.length - 1) {
            return time >= phase.start && time <= phase.end
          }
          return time >= phase.start && time < phase.end
        }) ?? null
      )
    },
    [phaseRanges],
  )

  const {
    toX,
    selectionPixels,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    lastDragFinishedAtRef,
  } = useTimelineZoomInteraction({
    maxTime,
    visibleRange,
    timelineWidth,
    disabled: isZoomed || disableZoomInteraction,
    onZoom: onDragZoom,
  })

  const toWidth = useCallback(
    (duration: number) => {
      const safeDuration = Math.max(duration, 0)
      const visibleDuration = Math.max(visibleRange.end - visibleRange.start, 1)
      return Math.max((safeDuration / visibleDuration) * timelineWidth, 1)
    },
    [timelineWidth, visibleRange.end, visibleRange.start],
  )

  const { visibleMinorTickTimes, visibleMinuteTickTimes } =
    useVisibleTimelineTicks({
      minorTickTimes,
      minuteTickTimes,
      visibleRange,
    })
  const orderedCastMarkers = useMemo(
    () =>
      [...castMarkers].sort((left, right) => {
        if (left.ignored === right.ignored) return 0
        return left.ignored ? -1 : 1
      }),
    [castMarkers],
  )
  const markerXPositions = useMemo(
    () => orderedCastMarkers.map((marker) => toX(marker.at)),
    [orderedCastMarkers, toX],
  )

  const hoverTimestamp =
    hoverTime !== null &&
    hoverTime !== undefined &&
    hoverTime >= visibleRange.start &&
    hoverTime <= visibleRange.end
      ? formatTimelineTimestamp(hoverTime)
      : null
  const hoverTimestampWidth = hoverTimestamp ? hoverTimestamp.length * 6 + 8 : 0
  const hoverTimestampX =
    hoverTime !== null && hoverTime !== undefined
      ? Math.max(
          hoverTimestampWidth / 2,
          Math.min(toX(hoverTime), timelineWidth - hoverTimestampWidth / 2),
        )
      : 0
  const addHoverTimestamp =
    addHoverTime !== null ? formatTimelineTimestamp(addHoverTime) : null
  const addHoverTimestampWidth = addHoverTimestamp
    ? addHoverTimestamp.length * 6 + 8
    : 0
  const addHoverTimestampX =
    addHoverTime !== null
      ? Math.max(
          addHoverTimestampWidth / 2,
          Math.min(
            toX(addHoverTime),
            timelineWidth - addHoverTimestampWidth / 2,
          ),
        )
      : 0
  const addHoverActionLabel = '+'
  const addHoverActionLabelWidth = addHoverActionLabel.length * 6 + 8
  const addHoverActionLabelX =
    addHoverTime !== null
      ? Math.max(
          addHoverActionLabelWidth / 2,
          Math.min(
            toX(addHoverTime),
            timelineWidth - addHoverActionLabelWidth / 2,
          ),
        )
      : 0
  const rangeStartLabel = formatTimelineTimestamp(visibleRange.start)
  const rangeEndLabel = formatTimelineTimestamp(visibleRange.end)
  const tickLabelY = rowsTop + rowsHeight + 14
  const visibleDeathMarkers = deathMarkers.flatMap((marker) => {
    if (marker.at < visibleRange.start || marker.at > visibleRange.end)
      return []
    return [marker]
  })
  const deathCountExtent = deathMarkers.reduce(
    (extent, marker) => ({
      min: Math.min(extent.min, marker.count),
      max: Math.max(extent.max, marker.count),
    }),
    { min: Infinity, max: 0 },
  )
  const deathCountOpacity = (count: number) => {
    if (deathCountExtent.max <= deathCountExtent.min) return 1
    const scale =
      (count - deathCountExtent.min) /
      (deathCountExtent.max - deathCountExtent.min)
    return 0.5 + 0.5 * scale
  }
  const startEdgeIsBound = visibleRange.start <= 0
  const endEdgeIsBound = visibleRange.end >= maxTime
  const startControlWidth = rangeStartLabel.length * 6 + 40
  const endControlWidth = rangeEndLabel.length * 6 + 40
  const edgeControlY = tickLabelY - 11

  const handleResetStartEdge = useCallback(
    (event: React.MouseEvent | React.PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setZoom(null)
    },
    [setZoom],
  )

  const handleResetEndEdge = useCallback(
    (event: React.MouseEvent | React.PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setZoom(null)
    },
    [setZoom],
  )

  const toTime = useCallback(
    (clientX: number, rect: DOMRect) => {
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      return Math.floor(clamp(nextTime, 0, maxTime))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )

  const onTimelineClick = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (disableZoomInteraction) return
      if (Date.now() - lastDragFinishedAtRef.current < 200) return
      if (Date.now() - lastBoundaryDragFinishedAtRef.current < 200) return
      if (isZoomed) {
        setZoom(null)
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0) return
      const phase = findPhaseAtTime(toTime(event.clientX, rect))
      if (!phase) return
      zoomToPhase(phase)
    },
    [
      disableZoomInteraction,
      findPhaseAtTime,
      isZoomed,
      lastDragFinishedAtRef,
      setZoom,
      toTime,
      zoomToPhase,
    ],
  )

  const onTimelineContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (disableZoomInteraction) return
      event.preventDefault()
      event.stopPropagation()
      setZoom(null)
    },
    [disableZoomInteraction, setZoom],
  )

  const onAddSurfacePointerMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      if (!enableAddCastInteraction) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0) {
        setAddHoverTime(null)
        setAddHoverBlocked(false)
        return
      }
      const nextTime = toTime(event.clientX, rect)
      const nextX = toX(nextTime)
      const blocked = markerXPositions.some(
        (markerX) => Math.abs(markerX - nextX) <= 8,
      )
      setAddHoverTime(nextTime)
      setAddHoverBlocked(blocked)
    },
    [enableAddCastInteraction, markerXPositions, toTime, toX],
  )

  const onAddSurfacePointerLeave = useCallback(() => {
    setAddHoverTime(null)
    setAddHoverBlocked(false)
  }, [])

  const onAddSurfaceClick = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!enableAddCastInteraction || !onAddCastAt) return
      if (addHoverBlocked || addHoverTime === null) return
      event.preventDefault()
      event.stopPropagation()
      onAddCastAt(addHoverTime)
    },
    [addHoverBlocked, addHoverTime, enableAddCastInteraction, onAddCastAt],
  )

  return (
    <div className='w-full' ref={rootRef} onContextMenu={onTimelineContextMenu}>
      <svg
        className='block w-full select-none'
        width='100%'
        height={svgHeight}
        viewBox={`0 0 ${timelineWidth} ${svgHeight}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClick={onTimelineClick}
      >
        <defs>
          <pattern
            id={hatchPatternId}
            width='4'
            height='4'
            patternUnits='userSpaceOnUse'
            patternTransform='rotate(45)'
          >
            <line
              x1='0'
              y1='0'
              x2='0'
              y2='4'
              stroke='black'
              strokeWidth='2'
              opacity='0.35'
            />
          </pattern>
        </defs>
        <g>
          {visiblePhaseRanges.length > 0 ? (
            visiblePhaseRanges.map((phase, index) => {
              const xStart = toX(phase.visibleStart)
              const xEnd = toX(phase.visibleEnd)
              const phaseWidth = Math.max(0, xEnd - xStart)
              const leftInset = index === 0 ? 0 : PHASE_CARD_GAP_PX / 2
              const rightInset =
                index === visiblePhaseRanges.length - 1
                  ? 0
                  : PHASE_CARD_GAP_PX / 2
              const x = xStart + leftInset
              const width = Math.max(0, phaseWidth - leftInset - rightInset)
              return (
                <g
                  key={`${phase.name}-${phase.start}-${phase.end}`}
                  className='group'
                >
                  <g className={className}>
                    <rect
                      x={x}
                      y={rowsTop}
                      width={width}
                      height={rowsHeight}
                      fill='currentColor'
                      rx={4}
                      ry={4}
                    />
                  </g>
                  <g className='text-border'>
                    <rect
                      x={x}
                      y={rowsTop}
                      width={width}
                      height={rowsHeight}
                      fill='transparent'
                      stroke='currentColor'
                      strokeWidth={1.25}
                      vectorEffect='non-scaling-stroke'
                      rx={8}
                      ry={8}
                    />
                  </g>
                  <foreignObject
                    x={x}
                    y={0}
                    width={width}
                    height={PHASE_LABEL_AREA_HEIGHT}
                  >
                    <div className='h-full w-full flex items-center justify-center gap-0.5 px-0.5 text-foreground'>
                      {onSwapPhase && phase.canMoveLeft ? (
                        <button
                          type='button'
                          className='inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground'
                          onPointerDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            onSwapPhase(phase.displayIndex, 'left')
                          }}
                        >
                          <ChevronLeft className='h-3.5 w-3.5' />
                        </button>
                      ) : null}
                      {disableZoomInteraction ? (
                        <span className='min-w-0 truncate text-[12px] leading-none'>
                          {phase.name}
                        </span>
                      ) : (
                        <span className='min-w-0 inline-flex items-center gap-1 truncate text-[12px] leading-none'>
                          <button
                            type='button'
                            className='truncate'
                            onPointerDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              zoomToPhase(phase)
                            }}
                          >
                            {phase.name}
                          </button>
                          {(phase.shortenedBy ?? 0) > 0 ? (
                            <PhaseShortenedLabel
                              shortenedBy={phase.shortenedBy ?? 0}
                              onReset={
                                onResetPhaseDuration
                                  ? () => onResetPhaseDuration(phase.phaseIndex)
                                  : undefined
                              }
                            />
                          ) : null}
                        </span>
                      )}
                      {onSwapPhase && phase.canMoveRight ? (
                        <button
                          type='button'
                          className='inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground'
                          onPointerDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            onSwapPhase(phase.displayIndex, 'right')
                          }}
                        >
                          <ChevronRight className='h-3.5 w-3.5' />
                        </button>
                      ) : null}
                    </div>
                  </foreignObject>
                </g>
              )
            })
          ) : (
            <g>
              <g className={className}>
                <rect
                  x={0}
                  y={rowsTop}
                  width={Math.max(timelineWidth, 1)}
                  height={rowsHeight}
                  fill='currentColor'
                  rx={4}
                  ry={4}
                />
              </g>
              <g className='text-border'>
                <rect
                  x={0}
                  y={rowsTop}
                  width={Math.max(timelineWidth, 1)}
                  height={rowsHeight}
                  fill='transparent'
                  stroke='currentColor'
                  strokeWidth={1.25}
                  vectorEffect='non-scaling-stroke'
                  rx={8}
                  ry={8}
                />
              </g>
            </g>
          )}
          {removedPhaseGroups.map((group) => {
            const clusterWidth = Math.max(
              group.phases.length * REMOVED_PHASE_BUTTON_WIDTH,
              REMOVED_PHASE_BUTTON_WIDTH,
            )
            const rawX = toX(group.at)
            const x = Math.max(
              0,
              Math.min(
                rawX - clusterWidth / 2,
                Math.max(timelineWidth - clusterWidth, 0),
              ),
            )
            return (
              <foreignObject
                key={`removed-phases-${group.at}-${group.phases
                  .map((phase) => phase.phaseIndex)
                  .join('-')}`}
                x={x}
                y={0}
                width={clusterWidth}
                height={PHASE_LABEL_AREA_HEIGHT}
                style={{ zIndex: 1 }}
              >
                <div className='h-full w-full flex items-center justify-center gap-0.5'>
                  {group.phases.map((phase) => (
                    <PhaseRemovedButton
                      key={`removed-phase-${phase.phaseIndex}`}
                      label={buildPhaseLabel(phase.phaseIndex, phaseDividers)}
                      onReset={
                        onResetPhaseDuration
                          ? () => onResetPhaseDuration(phase.phaseIndex)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </foreignObject>
            )
          })}
        </g>

        <g className='text-border'>
          {visibleMinorTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minor-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.45}
              />
            )
          })}
        </g>

        <g className='text-foreground'>
          {visibleMinuteTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minute-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.3}
              />
            )
          })}
        </g>

        <g>
          {onPhaseBoundaryDrag && onPhaseBoundaryDragEnd
            ? phaseBoundaryHandles.map((handle) => {
                const range = phaseRanges.find(
                  (phase) => phase.phaseIndex === handle.index,
                )
                if (!range || range.end <= range.start) return null
                if (
                  handle.at < visibleRange.start ||
                  handle.at > visibleRange.end
                ) {
                  return null
                }
                const x = toX(handle.at)
                return (
                  <g key={`phase-boundary-handle-${handle.index}`}>
                    <Tooltip
                      open={resizeTooltipHandleIndex === handle.index}
                      onOpenChange={(open) => {
                        if (suppressResizeTooltipRef.current) {
                          setResizeTooltipHandleIndex(null)
                          return
                        }
                        setResizeTooltipHandleIndex(open ? handle.index : null)
                      }}
                    >
                      <TooltipTrigger asChild>
                        <rect
                          x={x - 5}
                          y={rowsTop}
                          width={10}
                          height={rowsHeight}
                          fill='transparent'
                          className='cursor-col-resize'
                          onPointerDown={(event) =>
                            onBoundaryHandlePointerDown(event, handle.index)
                          }
                          onPointerEnter={() => {
                            if (!isDraggingPhaseRef.current) {
                              suppressResizeTooltipRef.current = false
                            }
                          }}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>Phase can be resized</TooltipContent>
                    </Tooltip>
                  </g>
                )
              })
            : null}
        </g>

        {emptyMessage && rows.length === 0 ? (
          <foreignObject
            x={0}
            y={rowsTop}
            width={Math.max(timelineWidth, 1)}
            height={rowsHeight}
          >
            <div className='h-full w-full flex items-center justify-center px-4'>
              <p className='text-center text-muted-foreground text-sm'>
                {emptyMessage}
              </p>
            </div>
          </foreignObject>
        ) : null}

        <g onPointerLeave={() => setHoveredSegment(null)}>
          {rows.map((row, rowIndex) => {
            const y = rowsTop + rowIndex * (ROW_HEIGHT + ROW_GAP)
            const rowEntries = row.entries
              .map((entry, entryIndex) => {
                const entryStart = entry.time
                const entryEnd = entry.time + entry.duration
                const visibleStart = Math.max(entryStart, visibleRange.start)
                const visibleEnd = Math.min(entryEnd, visibleRange.end)
                if (visibleEnd <= visibleStart) return null
                const previousEntry = row.entries[entryIndex - 1]
                const nextEntry = row.entries[entryIndex + 1]
                const showLabel =
                  !previousEntry ||
                  entry.time - previousEntry.time > CAST_LABEL_MIN_GAP_SECONDS
                const hasHiddenFollower =
                  Boolean(nextEntry) &&
                  nextEntry.time - entry.time <= CAST_LABEL_MIN_GAP_SECONDS
                const visibleSegments = resolveEntrySegments({
                  time: entry.time,
                  duration: entry.duration,
                  segments: entry.segments,
                }).flatMap((segment, segmentIndex) => {
                  if (segment.type === 'SPACE') return []
                  const segmentVisibleStart = Math.max(
                    segment.start,
                    visibleRange.start,
                  )
                  const segmentVisibleEnd = Math.min(
                    segment.end,
                    visibleRange.end,
                  )
                  if (segmentVisibleEnd <= segmentVisibleStart) return []
                  return [
                    {
                      key: `${row.spellId}-${entryIndex}-${segmentIndex}`,
                      type: segment.type,
                      x: toX(segmentVisibleStart),
                      width: toWidth(segmentVisibleEnd - segmentVisibleStart),
                      color: segment.color ?? row.color,
                      tooltip: segment.tooltip,
                      start: segment.start,
                      duration: segment.duration,
                    },
                  ]
                })
                const segments = visibleSegments.map(
                  (segment, segmentIndex) => {
                    const previousSegment = visibleSegments[segmentIndex - 1]
                    const nextSegment = visibleSegments[segmentIndex + 1]
                    const gapBefore =
                      !previousSegment ||
                      previousSegment.x + previousSegment.width <
                        segment.x - 0.5
                    const gapAfter =
                      !nextSegment ||
                      segment.x + segment.width < nextSegment.x - 0.5
                    return {
                      ...segment,
                      roundLeft: gapBefore,
                      roundRight: gapAfter,
                    }
                  },
                )
                return {
                  key: `${row.spellId}-${entryIndex}`,
                  x: toX(visibleStart),
                  width: toWidth(visibleEnd - visibleStart),
                  label: formatTimelineTimestamp(entry.time),
                  showLabel,
                  hasHiddenFollower,
                  spellId: row.spellId,
                  abilityName: row.name,
                  abilityColor: row.color,
                  segments,
                }
              })
              .filter((entry): entry is NonNullable<typeof entry> =>
                Boolean(entry),
              )

            return (
              <g key={`${row.spellId}-${row.name}`}>
                {rowEntries.map((entry) => {
                  return (
                    <g key={entry.key}>
                      {entry.segments.map((segment) => (
                        <EncounterEntrySegment
                          key={segment.key}
                          segment={segment}
                          y={y}
                          hatchPatternId={hatchPatternId}
                          onHover={(nextSegment) => {
                            if (!nextSegment) {
                              setHoveredSegment(null)
                              return
                            }
                            setHoveredSegment({
                              key: nextSegment.key,
                              x: nextSegment.x,
                              y,
                              width: nextSegment.width,
                              spellId: entry.spellId,
                              abilityName: entry.abilityName,
                              abilityColor: entry.abilityColor,
                              label: nextSegment.tooltip,
                              start: nextSegment.start,
                              duration: nextSegment.duration,
                            })
                          }}
                        />
                      ))}
                      {entry.showLabel && !entry.hasHiddenFollower ? (
                        <g className='text-accent-foreground pointer-events-none'>
                          <text
                            x={entry.x + 4}
                            y={y + 1 + ROW_HEIGHT / 2}
                            fill='currentColor'
                            fontSize={12}
                            dominantBaseline='middle'
                          >
                            {entry.label}
                          </text>
                        </g>
                      ) : null}
                    </g>
                  )
                })}
                <g className='text-accent-foreground pointer-events-none'>
                  {rowEntries.map((entry) =>
                    entry.showLabel && entry.hasHiddenFollower ? (
                      <text
                        key={`label-${entry.key}`}
                        x={entry.x + 4}
                        y={y + 1 + ROW_HEIGHT / 2}
                        fill='currentColor'
                        fontSize={12}
                        dominantBaseline='middle'
                      >
                        {entry.label}
                      </text>
                    ) : null,
                  )}
                </g>
              </g>
            )
          })}
          {hoveredSegment ? (
            <Tooltip open delayDuration={200}>
              <TooltipTrigger asChild>
                <rect
                  x={hoveredSegment.x}
                  y={hoveredSegment.y}
                  width={Math.max(hoveredSegment.width, 6)}
                  height={ROW_HEIGHT}
                  fill='transparent'
                  className='pointer-events-none'
                />
              </TooltipTrigger>
              <TooltipContent className='px-2 py-1.5'>
                <div className='flex items-start gap-2'>
                  <SpellIcon
                    spellId={hoveredSegment.spellId}
                    className='w-6 h-6 rounded border border-border shrink-0'
                  />
                  <div className='min-w-0'>
                    <div
                      className='font-medium leading-tight'
                      style={{ color: hoveredSegment.abilityColor }}
                    >
                      {hoveredSegment.abilityName}
                    </div>
                    {hoveredSegment.label ? (
                      <div className='text-xs text-muted-foreground leading-tight'>
                        <TimelineTooltipText text={hoveredSegment.label} />
                      </div>
                    ) : null}
                    <div className='text-xs text-muted-foreground leading-tight'>
                      {formatTimelineTimestamp(hoveredSegment.start)} –{' '}
                      {formatSegmentDuration(hoveredSegment.duration)}
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </g>

        {selectionPixels && !disableZoomInteraction && (
          <TimelineDragSelectionOverlay
            startX={selectionPixels.startX}
            endX={selectionPixels.endX}
            top={rowsTop}
            height={rowsHeight}
          />
        )}

        {enableAddCastInteraction ? (
          <rect
            x={0}
            y={rowsTop}
            width={timelineWidth}
            height={rowsHeight}
            fill='transparent'
            className={addHoverBlocked ? '' : 'cursor-copy'}
            onPointerMove={onAddSurfacePointerMove}
            onPointerLeave={onAddSurfacePointerLeave}
            onClick={onAddSurfaceClick}
          />
        ) : null}

        {visibleDeathMarkers.map((marker) => {
          const x = toX(marker.at)
          return (
            <foreignObject
              key={`death-skull-${marker.at}`}
              x={x - 8}
              y={tickLabelY - 11}
              width={16}
              height={12}
            >
              <div
                className='flex items-center justify-center text-destructive-foreground'
                style={{ opacity: deathCountOpacity(marker.count) }}
              >
                <Skull className='h-3 w-3' />
              </div>
            </foreignObject>
          )
        })}

        <g className='text-muted-foreground'>
          {minuteTickLabelTimes.map((tickAt) => {
            const x = toX(tickAt)
            const label = formatTimelineTimestamp(tickAt)
            const width = label.length * 7 + 6
            return (
              <g key={`minute-tick-label-${tickAt}`}>
                <rect
                  x={x - width / 2}
                  y={tickLabelY - 11}
                  width={width}
                  height={14}
                  rx={3}
                  fill='var(--color-card)'
                  fillOpacity={0.5}
                />
                <text
                  x={x}
                  y={tickLabelY}
                  fill='currentColor'
                  fontSize={12}
                  textAnchor='middle'
                >
                  {label}
                </text>
              </g>
            )
          })}
        </g>

        <g>
          {orderedCastMarkers.map((marker) => {
            if (
              marker.at < visibleRange.start ||
              marker.at > visibleRange.end
            ) {
              return null
            }
            const x = toX(marker.at)
            const markerLabelWidth = marker.label.length * 6 + 8
            return (
              <g key={marker.id}>
                <line
                  x1={x}
                  x2={x}
                  y1={rowsTop}
                  y2={rowsTop + rowsHeight}
                  stroke={
                    marker.ignored
                      ? 'var(--color-muted-foreground)'
                      : 'var(--color-info-foreground)'
                  }
                  strokeWidth={3}
                  opacity={marker.ignored ? 0.7 : 1}
                />
                <rect
                  x={x - 6}
                  y={rowsTop}
                  width={12}
                  height={rowsHeight}
                  fill='transparent'
                  className='cursor-ew-resize'
                  onPointerDown={marker.onPointerDown}
                  onClick={marker.onClick}
                  onContextMenu={marker.onContextMenu}
                />
                {marker.disableTooltip ? (
                  <rect
                    x={x - markerLabelWidth / 2}
                    y={rowsTop + rowsHeight + 2.5}
                    width={markerLabelWidth}
                    height={14}
                    fill={
                      marker.ignored
                        ? 'var(--color-muted)'
                        : 'var(--color-background)'
                    }
                    stroke={
                      marker.ignored
                        ? 'var(--color-muted-foreground)'
                        : 'var(--color-info-foreground)'
                    }
                    strokeWidth={1}
                    rx={2}
                    ry={2}
                    className='cursor-ew-resize'
                    onPointerDown={marker.onPointerDown}
                    onClick={marker.onClick}
                    onContextMenu={marker.onContextMenu}
                  />
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <rect
                        x={x - markerLabelWidth / 2}
                        y={rowsTop + rowsHeight + 2.5}
                        width={markerLabelWidth}
                        height={14}
                        fill={
                          marker.ignored
                            ? 'var(--color-muted)'
                            : 'var(--color-background)'
                        }
                        stroke={
                          marker.ignored
                            ? 'var(--color-muted-foreground)'
                            : 'var(--color-info-foreground)'
                        }
                        strokeWidth={1}
                        rx={2}
                        ry={2}
                        className='cursor-ew-resize'
                        onPointerDown={marker.onPointerDown}
                        onClick={marker.onClick}
                        onContextMenu={marker.onContextMenu}
                      />
                    </TooltipTrigger>
                    <TooltipContent className='text-xs text-muted-foreground'>
                      {marker.tooltip}
                    </TooltipContent>
                  </Tooltip>
                )}
                <text
                  x={x}
                  y={rowsTop + rowsHeight + 14}
                  fill={
                    marker.ignored
                      ? 'var(--color-muted-foreground)'
                      : 'var(--color-accent-foreground)'
                  }
                  fontSize={12}
                  textAnchor='middle'
                  className='pointer-events-none'
                >
                  {marker.label}
                </text>
              </g>
            )
          })}
        </g>

        {hoverTime !== null && hoverTime !== undefined && hoverTimestamp && (
          <g className='pointer-events-none'>
            <line
              x1={toX(hoverTime)}
              x2={toX(hoverTime)}
              y1={rowsTop}
              y2={rowsTop + rowsHeight}
              stroke='var(--color-accent-foreground)'
              strokeWidth={1}
            />
            <rect
              x={hoverTimestampX - hoverTimestampWidth / 2}
              y={rowsTop + rowsHeight + 2.5}
              width={hoverTimestampWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-border)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={hoverTimestampX}
              y={rowsTop + rowsHeight + 14}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {hoverTimestamp}
            </text>
          </g>
        )}

        {enableAddCastInteraction &&
        addHoverTime !== null &&
        !addHoverBlocked &&
        addHoverTimestamp !== null ? (
          <g className='pointer-events-none'>
            <line
              x1={toX(addHoverTime)}
              x2={toX(addHoverTime)}
              y1={rowsTop}
              y2={rowsTop + rowsHeight}
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              strokeDasharray='4 3'
              opacity={0.9}
            />
            <rect
              x={addHoverTimestampX - addHoverTimestampWidth / 2}
              y={rowsTop + rowsHeight + 2.5}
              width={addHoverTimestampWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={addHoverTimestampX}
              y={rowsTop + rowsHeight + 14}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {addHoverTimestamp}
            </text>
            <rect
              x={addHoverActionLabelX - addHoverActionLabelWidth / 2}
              y={1}
              width={addHoverActionLabelWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={addHoverActionLabelX}
              y={12}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {addHoverActionLabel}
            </text>
          </g>
        ) : null}

        {visibleDeathMarkers.map((marker) => {
          const x = toX(marker.at)
          return (
            <g key={`death-${marker.at}`}>
              <line
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='var(--color-destructive)'
                strokeWidth={marker.strokeWidth}
                opacity={marker.opacity}
                pointerEvents='none'
              />
              <foreignObject
                x={x - 14}
                y={tickLabelY + 3}
                width={28}
                height={12}
              >
                <div
                  className='text-center text-[10px] leading-none text-accent-foreground'
                  style={{ opacity: deathCountOpacity(marker.count) }}
                >
                  {marker.count}
                </div>
              </foreignObject>
            </g>
          )
        })}

        {isZoomed && !disableZoomInteraction && (
          <g className='text-muted-foreground'>
            {startEdgeIsBound ? (
              <text
                x={1}
                y={tickLabelY}
                fill='currentColor'
                fontSize={12}
                textAnchor='start'
              >
                {rangeStartLabel}
              </text>
            ) : (
              <ZoomEdgeControl
                x={1}
                y={edgeControlY}
                width={startControlWidth}
                label={rangeStartLabel}
                onReset={handleResetStartEdge}
              />
            )}
            {endEdgeIsBound ? (
              <text
                x={Math.max(timelineWidth - 1, 0)}
                y={tickLabelY}
                fill='currentColor'
                fontSize={12}
                textAnchor='end'
              >
                {rangeEndLabel}
              </text>
            ) : (
              <ZoomEdgeControl
                x={Math.max(timelineWidth - endControlWidth - 1, 0)}
                y={edgeControlY}
                width={endControlWidth}
                label={rangeEndLabel}
                onReset={handleResetEndEdge}
              />
            )}
          </g>
        )}
      </svg>
    </div>
  )
}

export default TimelineContainer
